/**
 * Hide Onboarding Dialog
 *
 * Confirmation modal shown when the user clicks "Hide onboarding" from the
 * 3-dot menu on the onboarding nav item. Hides the nav item and closes the
 * panel if onboarding is the active panel.
 */

import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Button from '@hapana/shared/web/components/shared/button/button.component.jsx'
import {
  BUTTON_VARIANTS,
  BUTTON_THEME,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { useOnboardingStore } from '_stores/onboarding.store'
import { useSupportAssistant } from '../support-assistant/support-assistant.hooks'
import { styles } from './hide-onboarding-dialog.styles'

interface HideOnboardingDialogProps {
  open: boolean
  onClose: () => void
}

const HideOnboardingDialog = ({ open, onClose }: HideOnboardingDialogProps) => {
  const { t } = useTranslation('core')
  const { hideOnboarding } = useOnboardingStore()
  const { activePanel, setIsOpen } = useSupportAssistant()

  const handleConfirm = () => {
    hideOnboarding()
    if (activePanel === 'onboarding') {
      setIsOpen(false)
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('onboarding.hideDialog.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('onboarding.hideDialog.description')}</DialogContentText>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button
          variant={BUTTON_VARIANTS.OUTLINED}
          theme={BUTTON_THEME.GRAY}
          label={t('onboarding.hideDialog.cancel')}
          onClick={onClose}
          data-testid="hide-onboarding-dialog-cancel"
        />
        <Button
          variant={BUTTON_VARIANTS.CONTAINED}
          theme={BUTTON_THEME.PRIMARY}
          label={t('onboarding.hideDialog.hide')}
          onClick={handleConfirm}
          data-testid="hide-onboarding-dialog-hide"
        />
      </DialogActions>
    </Dialog>
  )
}

export default HideOnboardingDialog
